import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  emailDocId,
  formatOnTimeRoleLabel,
  getAllowedOnTimePageKeys,
  getOnTimeLandingPath,
  normalizeOnTimeRole,
} from '../utils/access';

export const ACTIVE_APP_KEY = 'im.activeApp';
export const ACTIVE_ORG_KEY = 'im.activeOrgId';
export const ONTIME_SESSION_KEY = 'im.ontime.session';
export const LINK_PROFILE_SESSION_KEY = 'matrixos.link.accessProfile.v1';
export const ACTIVE_CONTEXT_EVENT = 'im-active-context-changed';

function orgHasOnTimeApp(orgData = {}) {
  const enabledApps = Array.isArray(orgData?.enabledApps)
    ? orgData.enabledApps.map((x) => String(x).toLowerCase())
    : [];
  const appsMap = orgData?.apps && typeof orgData.apps === 'object'
    ? Object.entries(orgData.apps)
        .filter(([, enabled]) => enabled === true)
        .map(([key]) => String(key).toLowerCase())
    : [];
  return (
    enabledApps.includes('ontime') ||
    enabledApps.includes('link') ||
    appsMap.includes('ontime') ||
    appsMap.includes('link') ||
    String(orgData?.defaultApp || '').toLowerCase() === 'ontime' ||
    String(orgData?.defaultApp || '').toLowerCase() === 'link'
  );
}

export function clearOnTimeSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ACTIVE_APP_KEY);
    window.sessionStorage.removeItem(ACTIVE_ORG_KEY);
    window.sessionStorage.removeItem(ONTIME_SESSION_KEY);
    window.sessionStorage.removeItem(LINK_PROFILE_SESSION_KEY);
    window.dispatchEvent(new Event(ACTIVE_CONTEXT_EVENT));
  } catch (_) {}
}

export function readOnTimeSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(ONTIME_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function readLinkProfileSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(LINK_PROFILE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function writeLinkProfileSession(session) {
  if (typeof window === 'undefined' || !session) return;
  try {
    window.sessionStorage.setItem(LINK_PROFILE_SESSION_KEY, JSON.stringify(session));
    window.dispatchEvent(new Event(ACTIVE_CONTEXT_EVENT));
  } catch (_) {}
}

export function clearLinkProfileSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(LINK_PROFILE_SESSION_KEY);
    window.dispatchEvent(new Event(ACTIVE_CONTEXT_EVENT));
  } catch (_) {}
}

export function writeOnTimeSession(session) {
  if (typeof window === 'undefined' || !session) return;
  try {
    window.sessionStorage.setItem(ACTIVE_APP_KEY, 'ontime');
    window.sessionStorage.setItem(ACTIVE_ORG_KEY, String(session.orgId || ''));
    window.sessionStorage.setItem(ONTIME_SESSION_KEY, JSON.stringify(session));
    window.dispatchEvent(new Event(ACTIVE_CONTEXT_EVENT));
  } catch (_) {}
}


function memberHasOnTimeApp(member = {}) {
  const allowed = new Set(['ontime', 'link']);
  const values = [];
  const collect = (raw) => {
    if (!raw) return;
    if (Array.isArray(raw)) {
      raw.forEach((item) => values.push(String(item || '').trim().toLowerCase()));
      return;
    }
    if (typeof raw === 'object') {
      Object.entries(raw).forEach(([key, enabled]) => {
        if (enabled === true || enabled === 'true' || enabled === 1) values.push(String(key || '').trim().toLowerCase());
      });
    }
  };
  collect(member.apps);
  collect(member.enabledApps);
  collect(member.appAccess);
  collect(member.appsMap);
  collect(member.access);
  collect(member.permissions);
  return values.some((value) => allowed.has(value));
}

function profileToClaims(user, orgId, profile = {}, fallbackRole = '') {
  const role = normalizeOnTimeRole(profile.onTimeRole || profile.role || fallbackRole || '');
  const claims = {
    orgId,
    role,
    onTimeRole: role,
    orgAdmin: role === 'owner',
    canManageOnTimeUsers: role === 'owner',
    apps: ['ontime'],
    defaultApp: 'ontime',
    active: profile.active !== false && profile.disabled !== true && profile.archived !== true && profile.removed !== true,
    uid: String(profile.uid || user?.uid || ''),
    email: String(profile.email || user?.email || '').trim().toLowerCase(),
    fullName: String(profile.fullName || profile.displayName || user?.displayName || '').trim(),
    surname: String(profile.surname || '').trim(),
    phone: String(profile.phone || '').trim(),
    onTimePages: Array.isArray(profile.onTimePages)
      ? profile.onTimePages
      : Array.isArray(profile.allowedPages)
        ? profile.allowedPages
        : Array.isArray(profile.tabsAllowed)
          ? profile.tabsAllowed
          : [],
  };
  claims.allowedPages = claims.onTimePages;
  claims.tabsAllowed = claims.onTimePages;
  claims.allowedOnTimePageKeys = getAllowedOnTimePageKeys(claims);
  claims.onTimeRoleLabel = formatOnTimeRoleLabel(role);
  return claims;
}

async function resolveDirectOnTimeOrg(user, email, uid) {
  const candidates = [];
  try {
    const token = await user?.getIdTokenResult?.(true);
    const orgId = String(token?.claims?.orgId || '').trim();
    if (orgId) candidates.push(orgId);
  } catch (_) {}

  try {
    const roleSnap = await getDoc(doc(db, 'userRoles', uid));
    const roleData = roleSnap.exists() ? roleSnap.data() || {} : null;
    const orgId = String(roleData?.orgId || roleData?.org || '').trim();
    if (orgId) candidates.push(orgId);
  } catch (_) {}

  for (const orgId of [...new Set(candidates)]) {
    try {
      const orgSnap = await getDoc(doc(db, 'orgs', orgId));
      if (!orgSnap.exists()) continue;
      const orgData = orgSnap.data() || {};
      if (!orgHasOnTimeApp(orgData)) continue;
      const profile = await findMemberProfile(orgId, email, uid);
      if (profile) {
        if (!memberHasOnTimeApp(profile)) return null;
        const claims = profileToClaims(user, orgId, profile);
        if (!claims.active) return null;
        return { app: 'ontime', orgId, email, uid, claims, landingPath: getOnTimeLandingPath(claims), savedAt: Date.now() };
      }
      if (String(orgData?.ownerEmail || '').trim().toLowerCase() === email) {
        const claims = profileToClaims(user, orgId, { email, fullName: String(orgData?.ownerName || user?.displayName || '').trim(), active: true }, 'owner');
        return { app: 'ontime', orgId, email, uid, claims, landingPath: getOnTimeLandingPath(claims), savedAt: Date.now() };
      }
    } catch (_) {}
  }
  return null;
}

async function findMemberProfile(orgId, email, uid) {
  const membersCol = collection(db, 'orgs', orgId, 'members');

  const direct = await getDoc(doc(db, 'orgs', orgId, 'members', emailDocId(email)));
  if (direct.exists()) return direct.data() || {};

  const byEmail = await getDocs(query(membersCol, where('email', '==', email), limit(1)));
  if (!byEmail.empty) return byEmail.docs[0].data() || {};

  if (uid) {
    const byUid = await getDocs(query(membersCol, where('uid', '==', uid), limit(1)));
    if (!byUid.empty) return byUid.docs[0].data() || {};
  }

  return null;
}

export async function resolveOnTimeSessionForUser(user) {
  const email = String(user?.email || '').trim().toLowerCase();
  const uid = String(user?.uid || '').trim();
  if (!email) return null;

  const direct = await resolveDirectOnTimeOrg(user, email, uid);
  if (direct) return direct;

  let orgSnap;
  try {
    orgSnap = await getDocs(collection(db, 'orgs'));
  } catch (error) {
    console.warn('[Link session] org scan blocked by rules; direct org lookup did not resolve access.', error);
    return null;
  }
  for (const orgDoc of orgSnap.docs) {
    const orgData = orgDoc.data() || {};
    if (!orgHasOnTimeApp(orgData)) continue;

    const profile = await findMemberProfile(orgDoc.id, email, uid);
    if (profile) {
      if (!memberHasOnTimeApp(profile)) return null;
      const claims = profileToClaims(user, orgDoc.id, profile);
      if (!claims.active) return null;
      return {
        app: 'ontime',
        orgId: orgDoc.id,
        email,
        uid,
        claims,
        landingPath: getOnTimeLandingPath(claims),
        savedAt: Date.now(),
      };
    }

    if (String(orgData?.ownerEmail || '').trim().toLowerCase() === email) {
      const claims = profileToClaims(user, orgDoc.id, {
        email,
        fullName: String(orgData?.ownerName || user?.displayName || '').trim(),
        active: true,
      }, 'owner');
      return {
        app: 'ontime',
        orgId: orgDoc.id,
        email,
        uid,
        claims,
        landingPath: getOnTimeLandingPath(claims),
        savedAt: Date.now(),
      };
    }
  }

  return null;
}
